import type { GlossaryConfig } from "@/modules/config";

function escapeAttribute(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Wraps whole-word, case-insensitive matches of each glossary term (keyed by term, per
// the `term-glossary` config) in a `.glossary-term` span carrying the definition as a
// data attribute, so a plain CSS :hover rule (see globals.css) can render the dotted
// underline + tooltip. A CSS-only tooltip is used instead of the React <Tooltip>
// component because this HTML is ultimately injected via dangerouslySetInnerHTML (see
// InsightBullets/StreamingText), not rendered as a React tree, so there's no element to
// attach a React tooltip's hover handlers to.
//
// Only replaces within the plain-text segments between tags (splitting on `<[^>]*>` and
// skipping the tag segments), so it never matches inside an existing tag's attributes,
// and StreamingText's own tokenizer is tag-aware, so the inserted <span> streams in as a
// whole unit rather than character-by-character.
export function applyGlossaryTerms(html: string, terms: GlossaryConfig | undefined): string {
    if (!html || !terms) return html;
    const entries = Object.entries(terms);
    if (entries.length === 0) return html;

    return html
        .split(/(<[^>]*>)/)
        .map((segment) => {
            if (segment.startsWith("<")) return segment;
            return entries.reduce((text, [term, definition]) => {
                const pattern = new RegExp(`\\b(${escapeRegExp(term)})\\b`, "gi");
                return text.replace(
                    pattern,
                    (match) => `<span class="glossary-term" data-definition="${escapeAttribute(definition)}">${match}</span>`
                );
            }, segment);
        })
        .join("");
}
